import { useMemo, useState, useEffect } from 'react';

export interface DecodedToken {
  sub: string;
  preferred_username: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  accountNumber?: string;
  trackingNumber?: string;
  country?: string;
  city?: string;
  mobileNumber?: string;
  authenticationMode?: string;
  registrationType?: 'existing_customer' | 'loan_applicant' | 'new_user';
  hasFullAccess?: boolean;
  [key: string]: any;
}

export function useDecodedToken(): DecodedToken | null {
  const [userInfo, setUserInfo] = useState<string | null>(() => {
    return localStorage.getItem('user_info');
  });

  // Function to decode and return user info
  const decodeUserInfo = (userInfoStr: string | null): DecodedToken | null => {
    if (!userInfoStr) return null;
    
    try {
      const userInfo = JSON.parse(userInfoStr);
      
      // Create a DecodedToken object from user info
      // Normalize potentially invalid string values like "undefined" or "null"
      const normalize = (value: any) => {
        if (value === undefined || value === null) return undefined;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed === '' || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
            return undefined;
          }
          return trimmed;
        }
        return value;
      };

      const decoded: DecodedToken = {
        sub: userInfo.id?.toString() || '',
        preferred_username: userInfo.username || '',
        email: userInfo.email,
        given_name: userInfo.firstName,
        family_name: userInfo.lastName,
        accountNumber: normalize(userInfo.accountNumber),
        trackingNumber: normalize(userInfo.trackingNumber),
        country: userInfo.country,
        city: userInfo.city,
        mobileNumber: userInfo.mobileNumber || userInfo.phone_number,
        authenticationMode: userInfo.authenticationMode,
        registrationType: (userInfo.userType ? String(userInfo.userType).toLowerCase() : undefined) as any,
        hasFullAccess: !!userInfo.hasFullAccess
      };
      
      // Derive registration type when missing or inconsistent with identifiers
      const hasAccountNumber = !!decoded.accountNumber;
      const hasTrackingNumber = !!decoded.trackingNumber;
      if (!decoded.registrationType) {
        decoded.registrationType = (hasAccountNumber
          ? 'existing_customer'
          : hasTrackingNumber
            ? 'loan_applicant'
            : 'new_user');
      } else {
        // Correct inconsistent states
        if (hasAccountNumber && decoded.registrationType !== 'existing_customer') {
          decoded.registrationType = 'existing_customer';
        } else if (!hasAccountNumber && hasTrackingNumber && decoded.registrationType === 'existing_customer') {
          decoded.registrationType = 'loan_applicant';
        }
      }

      // Determine access level based on derived registration type and identifiers
      if (decoded.registrationType === 'existing_customer' && hasAccountNumber) {
        // Existing customers with verified account numbers have full access
        decoded.hasFullAccess = true;
      } else if (decoded.registrationType === 'loan_applicant' && hasTrackingNumber) {
        // Loan applicants can track applications and apply for new loans, but limited account access
        decoded.hasFullAccess = false;
      } else if (decoded.registrationType === 'new_user') {
        // New users can create loan applications but have no account access
        decoded.hasFullAccess = false;
      } else {
        // Default to no access if registration type is unclear
        decoded.hasFullAccess = false;
      }
      
      return decoded;
    } catch (error) {
      console.error("Failed to parse user info:", error);
      return null;
    }
  };

  // Listen for custom storage events (when user_info is updated in the same tab)
  useEffect(() => {
    const handleCustomStorageChange = () => {
      const currentUserInfo = localStorage.getItem('user_info');

      setUserInfo(currentUserInfo);
    };

    window.addEventListener('userInfoChanged', handleCustomStorageChange);
    return () => window.removeEventListener('userInfoChanged', handleCustomStorageChange);
  }, []);

  // Return decoded user info
  return useMemo(() => {
    return decodeUserInfo(userInfo);
  }, [userInfo]);
}

// Export a function to manually refresh user info from localStorage
export const refreshUserInfoFromStorage = (): void => {
  // Force a re-render by dispatching a custom event
  window.dispatchEvent(new Event('userInfoChanged'));
}; 
